import { Badge } from '@ds/core';
import { dataDe, diaIso, horaDe, mesEAno, somarDias } from '@/comuns/formato';
import type { Agendamento } from '@/data/tipos';

export interface GradeDoMesProps {
    /** Qualquer dia do mês exibido. */
    diaDeReferencia: string;
    agendamentos: Agendamento[];
    onAbrirDia: (dia: string) => void;
    onAbrir: (agendamento: Agendamento) => void;
}

const CABECALHOS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

/**
 * Visão de mês.
 *
 * Não existia no protótipo: "Mês" mostrava a mesma semana da visão semanal. A
 * grade aqui é de verdade — o mês inteiro, começando na segunda, com os dias
 * das bordas em cinza para não fingir que pertencem a este mês.
 */
export function GradeDoMes({ diaDeReferencia, agendamentos, onAbrirDia, onAbrir }: GradeDoMesProps) {
    const referencia = dataDe(diaDeReferencia);
    const mes = referencia.getMonth();
    const primeiro = `${diaDeReferencia.slice(0, 8)}01`;
    const inicio = somarDias(primeiro, -((dataDe(primeiro).getDay() + 6) % 7));
    const hoje = diaIso();

    const dias = Array.from({ length: 42 }, (_, i) => somarDias(inicio, i));
    /* Seis semanas cobrem qualquer mês, mas a última costuma sobrar inteira:
       cortá-la evita uma faixa vazia que parece falha de carregamento. */
    const semanas: string[][] = [];
    for (let i = 0; i < dias.length; i += 7) {
        const semana = dias.slice(i, i + 7);
        if (i >= 28 && semana.every((d) => dataDe(d).getMonth() !== mes)) break;
        semanas.push(semana);
    }

    const doDia = (dia: string) => agendamentos.filter((a) => a.inicioEm.startsWith(dia));

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', borderBottom: '1px solid var(--border)' }}>
                {CABECALHOS.map((c) => (
                    <div key={c} style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)', borderLeft: '1px solid var(--border-subtle)' }}>
                        {c}
                    </div>
                ))}
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {semanas.map((semana) => (
                    <div key={semana[0]} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', borderBottom: '1px solid var(--border-subtle)' }}>
                        {semana.map((dia) => {
                            const doMes = dataDe(dia).getMonth() === mes;
                            const lista = doDia(dia);
                            return (
                                <div key={dia} style={{ minHeight: 108, padding: 8, borderLeft: '1px solid var(--border-subtle)', background: doMes ? 'transparent' : 'var(--surface-secondary)', display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    <button
                                        type="button" onClick={() => onAbrirDia(dia)}
                                        aria-label={`Abrir ${Number(dia.slice(8, 10))} de ${mesEAno(dia)}`}
                                        style={{
                                            alignSelf: 'flex-start', minWidth: 24, height: 24, padding: '0 6px', border: 0, borderRadius: '50%', cursor: 'pointer',
                                            background: dia === hoje ? 'var(--burgundy-700)' : 'transparent',
                                            color: dia === hoje ? '#fff' : doMes ? 'var(--text-primary)' : 'var(--text-tertiary)',
                                            fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {Number(dia.slice(8, 10))}
                                    </button>

                                    {lista.slice(0, 3).map((a) => (
                                        <button
                                            key={a.id} type="button" onClick={() => onAbrir(a)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '3px 6px', border: 0,
                                                borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left',
                                                background: a.status === 'cancelado' ? 'var(--surface-secondary)' : 'var(--surface-brand-soft)',
                                                color: a.status === 'cancelado' ? 'var(--text-tertiary)' : 'var(--text-brand)',
                                                fontFamily: 'var(--font-ui)', fontSize: 11.5, fontWeight: 500,
                                                textDecoration: a.status === 'cancelado' ? 'line-through' : 'none',
                                            }}
                                        >
                                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{horaDe(a.inicioEm)}</span>
                                            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.paciente}</span>
                                        </button>
                                    ))}

                                    {lista.length > 3 && (
                                        <button
                                            type="button" onClick={() => onAbrirDia(dia)}
                                            style={{ alignSelf: 'flex-start', border: 0, background: 'transparent', padding: '0 6px', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-secondary)' }}
                                        >
                                            <Badge tone="neutral">+{lista.length - 3}</Badge>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}
